import { UtilsService } from '../../../../@core/utils/utils.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ChartsService } from '../../../../@core/utils/charts.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { NbDialogService, NbCardModule, NbAlertModule, NbButtonModule } from '@nebular/theme';
import * as Highcharts from 'highcharts';
import { Subscription } from 'rxjs';

import { KPI } from '../../../../@core/api';
import {
  InvestmentChartOptionsService,
  LayoutService,
} from '../../../../@core/utils';
import { BaseChartComponent } from '../base-chart/base-chart.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-site-initiative-consumptions-chart',
  styleUrls: ['./site-initiative-consumptions-chart.component.scss'],
  templateUrl: './site-initiative-consumptions-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NbCardModule,
    NgClass,
    
    FontAwesomeModule,
    HighchartsChartModule,
    NbAlertModule,
    NbButtonModule,
    TranslateModule
  ],
})
export class SiteInitiativeConsumptionsChartComponent
  extends BaseChartComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() public initiative: KPI.MappedInitiative;

  private subscriptions: Subscription[] = [];
  public title: string = this.translate.instant('Relative weight of the action');
  public subtitle: string = '';
  private siteConsumptionsData: KPI.SiteConsumption;
  private colors: string[] = [];
  public totalConsumption: number = 0;

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    private investmentChartOptionsService: InvestmentChartOptionsService,
    protected router: Router,
    private chartsService: ChartsService,
    private translate: TranslateService,
    private utilsService: UtilsService,
    private cdRef: ChangeDetectorRef,
  ) {
    super(dialogService, layoutService, router);
  }

  public ngOnInit(): void {
    super.ngOnInit();
  }

  public ngOnChanges(): void {
    if (this.initiative) {
      this.loading = true;
      const multiPerimeter = this.initiative.site;
      if (multiPerimeter) {
        this.subscriptions.push(
          this.chartsService.getSiteConsumption(
            multiPerimeter.id,
          ).subscribe((siteConsumptionsData) => {
            if (siteConsumptionsData && Object.keys(siteConsumptionsData).length) {
              this.siteConsumptionsData = siteConsumptionsData;
              this.update();
            } else {
              this.loading = false;
              this.visible = false;
              this.cdRef.detectChanges();
            }
          }, () => {
            this.loading = false;
            this.visible = false;
            this.cdRef.detectChanges();
          }),
        );
      } else {
        this.loading = false;
        this.visible = false;
        this.cdRef.detectChanges();
      }
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  protected update(): void {
    this.totalConsumption = 0;
    const chartData = {};
    if (this.siteConsumptionsData?.perimeters?.length) {
      this.siteConsumptionsData.perimeters.map((p) => {
        if (p.trajectories?.length) {
          p.trajectories.map((t) => {
            if (t.energy_type && t.perimeter_consumptions?.length) {
              const lastTrajConsos: KPI.PerimeterTrajectoryConsumption[] = t.perimeter_consumptions.sort((a, b) =>
                b.year - a.year
              );
              if (lastTrajConsos.length) {
                const lastTrajCons = lastTrajConsos[0];
                if (!chartData[t.energy_type?.id]) {
                  chartData[t.energy_type?.id] = {
                    energy_color: t.energy_type.color,
                    energy_name: t.energy_type.name,
                    site_consumption: lastTrajCons.consumption,
                    year: lastTrajCons.year,
                  };
                } else {
                  chartData[t.energy_type?.id].site_consumption += lastTrajCons.consumption;
                }
              }
            }
          })
        }
      });

      const energyTypeIds: number[] = Object.keys(chartData).map((k) => Number(k));
      const data = [];
      const defaultColors = ['#BF9125', '#FFD966', '#1BB1F0'];
      this.colors = [];

      let initiativeSavings: number = 0;

      energyTypeIds.map((energyTypeId, i) => {
        if (chartData[energyTypeId]) {
          const initiativeEnergyTypeGain = this.initiative.gains.find((gain) => gain.energy_type_id === energyTypeId);
          if (initiativeEnergyTypeGain) {
            const initiativeEnergyTypeGSavings = initiativeEnergyTypeGain.theoretical_savings_full_year;
            const finalSiteConsumption =
              chartData[energyTypeId].site_consumption - initiativeEnergyTypeGSavings;
            data.push({
              name: this.translate.instant(
                'Last declared {energyType} consumption in {year}',
                {
                  energyType: chartData[energyTypeId].energy_name,
                  year: chartData[energyTypeId].year,
                },
              ) + ' : '
                + this.utilsService.formateNumber(Math.round(chartData[energyTypeId].site_consumption / 1000))
                + ' '
                + this.translate.instant('MWh')
                + ' ',
              y: finalSiteConsumption,
            });

            data.push({
              name: this.translate.instant('Of which theoretical initiative savings')
                + ' : '
                + this.utilsService.formateNumber(initiativeEnergyTypeGSavings)
                + ' '
                + this.translate.instant('kWh'),
              y: initiativeEnergyTypeGSavings,
              sliced: true,
              initiative: true,
            });

            initiativeSavings += initiativeEnergyTypeGSavings;
            this.totalConsumption += chartData[energyTypeId].site_consumption;

            if (chartData[energyTypeId].energy_color) {
              this.colors.push(
                this.changeOpacity(chartData[energyTypeId].energy_color),
              );
              this.colors.push(chartData[energyTypeId].energy_color);
            } else {
              this.colors.push(this.changeOpacity(defaultColors[i]));
              this.colors.push(defaultColors[i]);
            }

          } else {
            const siteConsumption = chartData[energyTypeId].site_consumption;
            data.push({
              name: this.translate.instant(
                'Last declared {energyType} consumption in {year}',
                {
                  energyType: chartData[energyTypeId].energy_name,
                  year: chartData[energyTypeId].year,
                },
              ) + ' : '
                + this.utilsService.formateNumber(Math.round(siteConsumption / 1000))
                + ' '
                + this.translate.instant('MWh'),
              y: siteConsumption,
            });

            this.totalConsumption += chartData[energyTypeId].site_consumption;

            if (chartData[energyTypeId].energy_color) {
              this.colors.push(
                this.changeOpacity(chartData[energyTypeId].energy_color),
              );
            } else {
              this.colors.push(this.changeOpacity(defaultColors[i]));
            }
          }
        }
      });

      // Condition needed to avoid dividing by zero which would result into an error
      if (initiativeSavings !== 0 && this.totalConsumption !== 0) {
        this.subtitle = this.translate.instant(
          'The action represents {percent} of annual consumption',
          {
            percent: this.utilsService.round2dec((initiativeSavings / this.totalConsumption) * 100, 1),
          }
        );
      }

      // Initialize series for each statuses
      const series = [{
        type: 'pie',
        name: '',
        data,
      }];

      // Update chart options
      this.options = {
        ...this.getOptions(),
        // @ts-ignore
        xAxis: {
          ...this.getOptions().xAxis,
          tickWidth: 0,
        },
        series: (series as Highcharts.SeriesColumnOptions[]),
      };

      this.loading = false;
      this.reflow();
      this.cdRef.detectChanges();
    } else {
      this.visible = false;
      this.loading = false;
      this.cdRef.detectChanges();
    }
  }

  protected getOptions(): Highcharts.Options {
    const colors = this.colors;
    const utilsService = this.utilsService;
    const translate = this.translate;

    const commonOptions = this.investmentChartOptionsService
      ? this.investmentChartOptionsService.getCommonOptions()
      : { colors: [] };

    return {
      ...super.getOptions(),
      ...commonOptions,
      title: {
        text: this.title,
        align: 'left',
        style: super.getOptions().title.style,
      },
      subtitle: {
        text: this.subtitle,
        align: 'left',
        style: {
          fontSize: '0.9375rem',
          fontWeight: 'normal',
          color: '#000000',
          fontFamily: 'Roboto, sans-serif',
        }
      },
      chart: {
        ...commonOptions.chart,
      },
      colors,
      tooltip: {
        formatter: function () {
          return this.key.toString()
        }
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          borderWidth: 0,
        },
        series: {
          dataLabels: {
            enabled: true,
            overflow: 'justify',
            inside: true,
            style: {
              fontWeight: 'normal',
              fontSize: '12px',
              color: '#4b4b4b',
              fontFamily: 'Roboto, sans-serif',
              textOutline: null,
            },
            formatter: function () {
              if (this.y && this.point['initiative']) {
                return utilsService.formateNumber(this.y)
                  + ' '
                  + translate.instant(_('kWh'))
                  + '<br/>'
                  + utilsService.round2dec(this.point['percentage'], 1)
                  + '%';
              } else {
                return '';
              }
            }
          },
        },
        pie: {
          allowPointSelect: true,
          showInLegend: true,
          dataLabels: {
            connectorColor: 'transparent'
          }
        },
      },
      yAxis: {
        title: null,
        gridLineWidth: 0,
        labels: {
          enabled: false,
        },
        maxPadding: 0.3,
      },
      series: [],
    };
  }

  private changeOpacity(color: string): string {
    const _opacity = Math.round(Math.min(Math.max(0.6, 0), 1) * 255);
    return color + _opacity.toString(16).toUpperCase();
  }

  protected getChartOptions(): Highcharts.ChartOptions {
    return {
      ...super.getChartOptions(),
      type: 'pie',
    };
  }
}
